import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const FeaturePayRent = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pay Rent & Earn 2% Rewards | LOCU"
        description="Transform your largest monthly expense into rewards. Earn 2% LOCU points on every rent payment — that's up to ₹7,200/year on ₹30K rent. No fees, instant points."
        path="/feature/pay-rent"
        keywords="pay rent earn rewards, rent cashback India, 2% rent rewards, LOCU pay rent"
      />
      {/* Hero Section */}
      <div className="bg-black text-white py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Pay rent.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
              Earn rewards.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto mb-12">
            Transform your largest monthly expense into valuable LOCU points. 
            2% back on every rent payment.
          </p>
          {!isAuthenticated && (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Start Earning
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Calculator Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-black text-white rounded-3xl p-12 text-center">
            <div className="text-7xl md:text-8xl font-bold mb-4">2%</div>
            <div className="text-2xl text-gray-400 mb-8">Back on every rent payment</div>
            <div className="grid grid-cols-3 gap-8 border-t border-gray-800 pt-8">
              <div>
                <div className="text-3xl font-bold mb-1">₹30K</div>
                <div className="text-gray-500">Monthly Rent</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">600</div>
                <div className="text-gray-500">Points/Month</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-1">₹7,200</div>
                <div className="text-gray-500">Yearly Value</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Simple as 1-2-3
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Upload Receipt</h3>
              <p className="text-gray-600">Take a photo of your rent receipt or bank transfer confirmation.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Auto Verification</h3>
              <p className="text-gray-600">Our system verifies your payment instantly. No waiting, no hassle.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Points Credited</h3>
              <p className="text-gray-600">2% of your rent amount is added to your LOCU balance immediately.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why LOCU for rent?
              </h2>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">No fees, ever</h4>
                    <p className="text-gray-600">Free to use. We don't charge you for earning rewards.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Automatic tracking</h4>
                    <p className="text-gray-600">See your rent history, points earned, and streaks in one view.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Use points instantly</h4>
                    <p className="text-gray-600">No waiting periods. Redeem at local businesses right away.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gray-100 rounded-3xl p-8">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">₹7,200</div>
                <div className="text-gray-600 mb-6">Example yearly rewards</div>
                <div className="space-y-3 text-left">
                  <div className="flex justify-between py-2 border-b border-gray-300">
                    <span>Monthly Rent</span>
                    <span className="font-semibold">₹30,000</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-300">
                    <span>Points per month</span>
                    <span className="font-semibold">600</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-300">
                    <span>Annual points</span>
                    <span className="font-semibold">7,200</span>
                  </div>
                  <div className="flex justify-between py-2 text-green-600 font-semibold">
                    <span>Value</span>
                    <span>₹7,200</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Start earning on rent today
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Start earning rewards on your rent. It only takes a few minutes.
          </p>
          {!isAuthenticated ? (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Sign Up Free
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={() => navigate('/pay-rent')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Pay Rent Now
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturePayRent;
